/**
 * WebGLPipeline - WebGL post-processing pipeline using EffectComposer.
 * Manages effect composition, hidden/excluded objects, and render passes.
 */

import { EffectComposer, RenderPass } from 'postprocessing';
import * as THREE from 'three';

import { Delegate } from '../../../utils/Delegate.js';
import { GenesysScene } from '../../GenesysScene.js';
import { IPostProcessPipeline, type PipelineHooks } from '../IPostProcessPipeline.js';
import { EffectPassWrapper } from '../utils/EffectPassWrapper.js';

import type { IPostProcessEffect } from '../IPostProcessEffect.js';
import type { BaseEffectConfig, EffectContext, WebGLEffectData } from '../IPostProcessEffect.js';

/**
 * WebGL post-processing pipeline using EffectComposer from the postprocessing library.
 */
export class WebGLPipeline extends IPostProcessPipeline {
  override readonly rendererType = 'webgl' as const;
  override readonly hooks: PipelineHooks;

  protected renderer: THREE.WebGLRenderer;
  protected composer: EffectComposer | null = null;
  protected renderPass: RenderPass | null = null;
  protected enabled = true;

  // Effect management
  protected effects = new Map<string, IPostProcessEffect>();
  protected effectConfigs = new Map<string, BaseEffectConfig>();
  protected effectWrappers = new Map<string, EffectPassWrapper>();
  protected effectPasses = new Map<string, any[]>(); // Additional passes (e.g., NormalPass)

  // Scene/camera tracking
  protected scene: THREE.Scene | null = null;
  protected camera: THREE.Camera | null = null;
  protected lastWindowSize: { width: number; height: number } | null = null;

  // Hidden/excluded objects
  protected hiddenObjects: Set<THREE.Object3D> = new Set();
  protected excludedObjects: Set<THREE.Object3D> = new Set();
  protected excludedLayer = 30;
  protected hiddenToRestore: THREE.Object3D[] = [];
  protected overlayScene = new GenesysScene();

  // Bound render function for performance
  protected boundOriginalRender: ((renderer: any, writeBuffer: any, readBuffer: any, deltaTime: any, maskActive: any) => void) | null = null;

  constructor(renderer: THREE.WebGLRenderer) {
    super();
    this.renderer = renderer;

    // Initialize hooks
    this.hooks = {
      onBeforeRender: new Delegate(),
      onAfterRender: new Delegate(),
      onEffectCreated: new Delegate(),
      onEffectDestroyed: new Delegate(),
      onPipelineRebuild: new Delegate(),
    };
  }

  public override registerEffect(effect: IPostProcessEffect): void {
    if (this.effects.has(effect.type)) {
      console.warn(`[WebGLPipeline] Effect "${effect.type}" already registered. Overwriting.`);
      this.unregisterEffect(effect.type);
    }

    this.effects.set(effect.type, effect);

    // Initialize with default config
    const defaultConfig = effect.getDefaultConfig();
    this.effectConfigs.set(effect.type, defaultConfig);

    // Rebuild composer if already initialized
    if (this.composer) {
      this.rebuildComposer();
    }

    this.hooks.onEffectCreated.invoke(effect, defaultConfig);
  }

  public override unregisterEffect(type: string): void {
    const effect = this.effects.get(type);
    if (!effect) return;

    // Dispose effect wrapper
    const wrapper = this.effectWrappers.get(type);
    wrapper?.dispose();
    this.effectWrappers.delete(type);

    // Dispose additional passes
    const passes = this.effectPasses.get(type);
    if (passes) {
      for (const pass of passes) {
        pass.dispose?.();
      }
      this.effectPasses.delete(type);
    }

    // Remove from maps
    this.effects.delete(type);
    this.effectConfigs.delete(type);

    // Rebuild composer
    if (this.composer) {
      this.rebuildComposer();
    }

    this.hooks.onEffectDestroyed.invoke(effect);
  }

  public override getEffect(type: string): IPostProcessEffect | null {
    return this.effects.get(type) ?? null;
  }

  public override getAllEffects(): ReadonlyArray<IPostProcessEffect> {
    return Array.from(this.effects.values());
  }

  public override configureEffect(type: string, config: Partial<BaseEffectConfig> & Record<string, unknown>): void {
    const currentConfig = this.effectConfigs.get(type);
    if (!currentConfig) {
      console.warn(`[WebGLPipeline] Effect "${type}" not found.`);
      return;
    }

    const oldConfig = { ...currentConfig };
    const updatedConfig = { ...currentConfig, ...config };
    this.effectConfigs.set(type, updatedConfig);

    // Structural changes (e.g. toggling AO) add/remove composer passes and must rebuild.
    const effect = this.effects.get(type);
    if (effect?.requiresRebuild?.(oldConfig, updatedConfig)) {
      this.rebuildComposer();
      return;
    }

    // Apply config to existing effect
    const wrapper = this.effectWrappers.get(type);

    if (effect && wrapper) {
      if (effect.applyWebGLConfig) {
        const effectData: WebGLEffectData = {
          effects: wrapper.getEffects(),
          passes: this.effectPasses.get(type),
        };
        effect.applyWebGLConfig(effectData, updatedConfig);
      }

      // Apply renderer-level config changes (e.g., tone mapping exposure)
      if (effect.applyRendererConfig) {
        effect.applyRendererConfig(updatedConfig, this.renderer);
      }
    }

    // Update pass enabled state
    this.syncPassEnabled();
  }

  public override getEffectConfig(type: string): BaseEffectConfig | null {
    return this.effectConfigs.get(type) ?? null;
  }

  public override setEffectEnabled(type: string, enabled: boolean): void {
    const config = this.effectConfigs.get(type);
    if (config) {
      config.enabled = enabled;
      this.rebuildComposer();
    }
  }

  // ============= Rendering =============

  @THREE.profile
  public override render(scene: THREE.Scene, camera: THREE.Camera, windowSize: { width: number; height: number }): void {
    if (!this.enabled) {
      this.renderer.render(scene, camera);
      return;
    }

    // Update scene/camera if changed
    if (this.scene !== scene || this.camera !== camera) {
      this.scene = scene;
      this.camera = camera;
      if (this.renderPass) {
        this.renderPass.mainScene = scene;
        this.renderPass.mainCamera = camera;
      }
      this.rebuildComposer();
    }

    // Initialize composer if not already initialized
    if (!this.composer || !this.renderPass) {
      this.initializeComposer();
    }

    // If still no composer (e.g., renderer not ready), fallback to direct render
    if (!this.composer || !this.renderPass) {
      this.renderer.render(scene, camera);
      return;
    }

    // Resize composer if window size changed
    if (!this.lastWindowSize || this.lastWindowSize.width !== windowSize.width || this.lastWindowSize.height !== windowSize.height) {
      THREE.ProfilerService.begin('WebGLPipeline.render.setSize');
      this.setSize(windowSize.width, windowSize.height);
      this.lastWindowSize = { width: windowSize.width, height: windowSize.height };
      THREE.ProfilerService.end('WebGLPipeline.render.setSize');
    }

    THREE.ProfilerService.begin('WebGLPipeline.render.onBeforeRender');
    this.hooks.onBeforeRender.invoke(scene, camera);
    THREE.ProfilerService.end('WebGLPipeline.render.onBeforeRender');

    THREE.ProfilerService.begin('WebGLPipeline.render.composerRender');
    this.composer.render();
    THREE.ProfilerService.end('WebGLPipeline.render.composerRender');

    THREE.ProfilerService.begin('WebGLPipeline.render.onAfterRender');
    this.hooks.onAfterRender.invoke(scene, camera);
    THREE.ProfilerService.end('WebGLPipeline.render.onAfterRender');
  }

  public override setSize(width: number, height: number): void {
    this.composer?.setSize(width, height);
  }

  public override setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public override isEnabled(): boolean {
    return this.enabled;
  }

  public override dispose(): void {
    // Dispose composer
    this.composer?.dispose();
    this.composer = null;

    // Dispose all effect wrappers
    for (const wrapper of this.effectWrappers.values()) {
      wrapper.dispose();
    }
    this.effectWrappers.clear();

    // Dispose additional passes
    for (const passes of this.effectPasses.values()) {
      for (const pass of passes) {
        pass.dispose?.();
      }
    }
    this.effectPasses.clear();

    // Dispose effects
    for (const effect of this.effects.values()) {
      effect.dispose?.();
    }
    this.effects.clear();

    this.effectConfigs.clear();
    this.hiddenObjects.clear();
    this.excludedObjects.clear();
    this.scene = null;
    this.camera = null;
  }

  // ============= Public API for hidden/excluded objects =============

  /** Add objects to hide from rendering */
  override addHiddenObjects(...objects: THREE.Object3D[]): void {
    for (const obj of objects) this.hiddenObjects.add(obj);
  }

  /** Remove objects from hidden list */
  override removeHiddenObjects(...objects: THREE.Object3D[]): void {
    for (const obj of objects) this.hiddenObjects.delete(obj);
  }

  /** Clear all hidden objects */
  override clearHiddenObjects(): void {
    this.hiddenObjects.clear();
  }

  /** Add objects to exclude from post-processing */
  override addExcludedObjects(...objects: THREE.Object3D[]): void {
    for (const obj of objects) {
      if (this.excludedObjects.has(obj)) continue;
      this.excludedObjects.add(obj);
      obj.traverse(child => {
        child.layers.enable(this.excludedLayer);
      });
    }
  }

  /** Remove objects from exclusion list */
  override removeExcludedObjects(...objects: THREE.Object3D[]): void {
    for (const obj of objects) {
      if (!this.excludedObjects.has(obj)) continue;
      this.excludedObjects.delete(obj);
      obj.traverse(child => {
        child.layers.disable(this.excludedLayer);
      });
    }
  }

  /** Clear all excluded objects */
  override clearExcludedObjects(): void {
    for (const obj of this.excludedObjects) {
      obj.traverse(child => {
        child.layers.disable(this.excludedLayer);
      });
    }
    this.excludedObjects.clear();
  }

  /** Set the layer used for excluded objects */
  override setExcludedLayer(layer: number): void {
    this.excludedLayer = Math.max(0, Math.min(31, layer));
  }

  /** Get the excluded layer */
  override getExcludedLayer(): number {
    return this.excludedLayer;
  }

  /** Get excluded objects set */
  override getExcludedObjects(): ReadonlySet<THREE.Object3D> {
    return this.excludedObjects;
  }

  // ============= Private implementation =============

  @THREE.profile
  protected initializeComposer(): void {
    if (!this.scene || !this.camera) return;

    // Dispose old composer
    this.composer?.dispose();

    // Drop tracked passes from the previous build. createAllEffects() only adds enabled
    // effects, so without clearing these maps would retain stale entries (e.g. AO's
    // NormalPass) after an effect is disabled.
    for (const wrapper of this.effectWrappers.values()) {
      wrapper.dispose();
    }
    this.effectWrappers.clear();

    for (const passes of this.effectPasses.values()) {
      for (const pass of passes) {
        pass.dispose?.();
      }
    }
    this.effectPasses.clear();

    // Check renderer capabilities
    const capabilities = this.renderer.capabilities;
    if (!capabilities) {
      console.warn('[WebGLPipeline] Renderer capabilities not available.');
      return;
    }

    const isWebGL2 = capabilities.isWebGL2;
    const supportHalfFloat = isWebGL2 && this.renderer.extensions.has('EXT_color_buffer_float');
    const maxSamples = capabilities.maxSamples;

    // Create composer
    this.composer = new EffectComposer(this.renderer, {
      frameBufferType: supportHalfFloat ? THREE.HalfFloatType : undefined,
      multisampling: isWebGL2 ? Math.min(4, maxSamples) : 0,
    });

    // Create render pass
    this.createRenderPass();

    // Create all effects
    this.createAllEffects();

    // Sync enabled state
    this.syncPassEnabled();

    this.hooks.onPipelineRebuild.invoke();
  }

  @THREE.profile
  protected rebuildComposer(): void {
    this.initializeComposer();
  }

  protected createRenderPass(): void {
    if (!this.composer || !this.scene || !this.camera) return;

    this.renderPass = new RenderPass(this.scene, this.camera);
    this.boundOriginalRender = this.renderPass.render.bind(this.renderPass);

    // Override render to handle hidden objects
    this.renderPass.render = (renderer, writeBuffer, readBuffer, deltaTime, maskActive) => {
      THREE.ProfilerService.begin('WebGLPipeline.renderPass.hideObjects');
      this.hiddenToRestore.length = 0;

      for (const object of this.hiddenObjects) {
        if (object?.visible) {
          object.visible = false;
          this.hiddenToRestore.push(object);
        }
      }
      THREE.ProfilerService.end('WebGLPipeline.renderPass.hideObjects');

      this.boundOriginalRender!(renderer, writeBuffer, readBuffer, deltaTime, maskActive);

      THREE.ProfilerService.begin('WebGLPipeline.renderPass.restoreHiddenObjects');
      for (const object of this.hiddenToRestore) {
        if (object) object.visible = true;
      }
      THREE.ProfilerService.end('WebGLPipeline.renderPass.restoreHiddenObjects');
    };

    this.composer.addPass(this.renderPass);
  }

  @THREE.profile
  protected createAllEffects(): void {
    const composer = this.composer;
    if (!composer || !this.scene || !this.camera) return;

    // Sort effects by order
    const sortedEffects = Array.from(this.effects.values()).sort((a, b) => {
      const aConfig = this.effectConfigs.get(a.type);
      const bConfig = this.effectConfigs.get(b.type);
      const aOrder = aConfig?.order ?? a.order;
      const bOrder = bConfig?.order ?? b.order;
      return aOrder - bOrder;
    });

    for (const effect of sortedEffects) {
      const config = this.effectConfigs.get(effect.type);
      // Skip disabled effects so auxiliary passes (e.g. AO's NormalPass) are not added
      // to the composer and do not trigger an extra full scene render each frame.
      if (!config || !config.enabled) continue;

      const context: EffectContext = {
        scene: this.scene,
        camera: this.camera,
        renderer: this.renderer,
      };

      const effectData = effect.createWebGLEffect(config, context);
      if (!effectData) continue;

      // Add additional passes (e.g., NormalPass for SSAO)
      if (effectData.passes) {
        for (const pass of effectData.passes) {
          composer.addPass(pass);
        }
        this.effectPasses.set(effect.type, effectData.passes);
      }

      // Create effect wrapper
      if (effectData.effects.length > 0) {
        const wrapper = new EffectPassWrapper(this.camera, effectData.effects);
        this.effectWrappers.set(effect.type, wrapper);
        const effectPass = wrapper.getPass();
        // EffectPass sets needsDepthTexture in updateMaterial() which runs in initialize().
        // Composer only calls initialize() in setRenderer() when passes were empty, so we must
        // initialize before addPass so the composer creates the depth texture for SSAO etc.
        const alpha = this.renderer.getContext().getContextAttributes()?.alpha ?? false;
        const frameBufferType = composer.inputBuffer.texture.type;
        effectPass.initialize(this.renderer, alpha, frameBufferType);
        composer.addPass(effectPass);
      }
    }
  }

  protected syncPassEnabled(): void {
    if (!this.composer) return;

    for (const [type, wrapper] of this.effectWrappers) {
      const config = this.effectConfigs.get(type);
      if (config?.enabled) {
        wrapper.enableEffect();
      } else {
        wrapper.disableEffect();
      }
    }

    this.updateRenderToScreen();
  }

  protected updateRenderToScreen(): void {
    if (!this.composer) return;

    let lastEnabledPass: any = null;

    for (const pass of this.composer.passes) {
      if (pass.enabled) {
        lastEnabledPass = pass;
      }
    }

    for (const pass of this.composer.passes) {
      pass.renderToScreen = pass === lastEnabledPass;
    }
  }

  protected hasEnabledEffects(): boolean {
    for (const config of this.effectConfigs.values()) {
      if (config.enabled) return true;
    }
    return false;
  }

  @THREE.profile
  protected renderExcludedObjects(objects: THREE.Object3D[], scene: THREE.Scene, camera: THREE.Camera): void {
    this.overlayScene.children.length = 0;
    const objectData: Array<{ obj: THREE.Object3D; parent: THREE.Object3D | null; matrixAutoUpdate: boolean }> = [];

    for (const obj of objects) {
      objectData.push({
        obj,
        parent: obj.parent,
        matrixAutoUpdate: obj.matrixAutoUpdate,
      });

      obj.updateWorldMatrix(true, true);
      obj.matrixAutoUpdate = false;
      obj.visible = true;
      this.overlayScene.add(obj);
    }

    const prevAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = false;
    this.renderer.clearDepth();
    this.renderer.render(this.overlayScene, camera);
    this.renderer.autoClear = prevAutoClear;

    for (const { obj, parent, matrixAutoUpdate } of objectData) {
      if (parent) parent.add(obj);
      obj.matrixAutoUpdate = matrixAutoUpdate;
    }
  }
}
